export type ServiceTone = 'orange' | 'sky' | 'grape' | 'olive' | 'mustard';

/** Item de lista com termo opcional em negrito seguido de descrição. */
export type SectionListItem = {
  term?: string;
  text: string;
};

/** Bloco de conteúdo rico dentro de uma seção: parágrafo ou lista. */
export type SectionBlock =
  | { kind: 'p'; text: string }
  | { kind: 'list'; items: SectionListItem[] };

export type ServiceSection = {
  heading: string;
  /** Seção simples (legado): parágrafo único. */
  body?: string;
  /** Conteúdo rico: parágrafos e listas com lead em negrito. Tem precedência sobre `body`. */
  blocks?: SectionBlock[];
};

export type Service = {
  slug: string;
  title: string;
  /** Title tag (SEO, ≤60 char). Fallback: `${title} — Educa Legal`. */
  seoTitle?: string;
  tagline: string;
  tone: ServiceTone;
  h1: string;
  metaDescription: string;
  /** Lead do hero. Array → múltiplos parágrafos. */
  intro: string | string[];
  offers: string[];
  sections: ServiceSection[];
  cta: string;
  relatedPostSlugs?: string[];
};

export const services: Service[] = [
  {
    slug: 'reforma-tributaria-escolas',
    title: 'Reforma Tributária para Escolas',
    seoTitle: 'Reforma Tributária para Escolas | Educa Legal',
    tagline:
      'Adequação societária e fiscal ao novo modelo tributário brasileiro — para que sua instituição de ensino atravesse a transição para o IBS e a CBS com segurança e sem perder competitividade.',
    tone: 'orange',
    h1: 'Reforma Tributária para Escolas — Adequação Societária e Fiscal ao Novo Modelo Tributário Brasileiro',
    metaDescription:
      'Adequação societária e fiscal à Reforma Tributária. Proteja sua escola dos impactos do IBS e CBS. Consultoria especializada em todo o Brasil.',
    intro:
      'A Reforma Tributária brasileira representa a maior mudança no sistema fiscal do país em décadas — e as instituições de ensino privado estão entre os segmentos que mais precisam de atenção especializada nessa transição. A substituição do PIS, Cofins, ISS e ICMS pelo IBS e pela CBS altera de forma significativa a carga tributária, o fluxo de caixa e a estrutura societária de muitas escolas.',
    offers: [
      'Diagnóstico do impacto da Reforma Tributária na operação da sua instituição',
      'Análise da estrutura societária atual e recomendações de adequação ao novo modelo',
      'Planejamento fiscal para o período de transição — minimizando impactos e aproveitando oportunidades',
      'Revisão de contratos e precificação considerando o novo regime tributário',
      'Parceria com a LG Contábil para análise integrada dos impactos jurídicos e contábeis',
      'Monitoramento contínuo das regulamentações complementares à Reforma',
      'Análise do impacto da Reforma Tributária na estrutura de distribuição de lucros e dividendos do mantenedor',
      'Planejamento patrimonial integrado — alinhando a reestruturação da escola com a proteção e organização do patrimônio pessoal e familiar do gestor',
    ],
    sections: [
      {
        heading: 'Por que agir agora',
        body: 'A transição para o novo sistema tributário será gradual — mas as decisões de estrutura societária e de planejamento fiscal precisam ser tomadas antes que as mudanças estejam totalmente em vigor. Escolas que se anteciparem terão mais tempo para ajustar contratos, revisar precificação e reorganizar sua estrutura com menor custo e menor risco.',
      },
      {
        heading: 'A Reforma Tributária não afeta só a escola — afeta também o patrimônio do mantenedor',
        blocks: [
          {
            kind: 'p',
            text: 'Para a maioria dos gestores e proprietários de escolas privadas, o patrimônio pessoal e o patrimônio da instituição estão profundamente conectados. Imóveis que a escola ocupa, estruturas de holding familiar, distribuição de lucros e dividendos, planejamento sucessório — tudo isso é impactado diretamente pelas mudanças que a Reforma Tributária introduz no sistema fiscal brasileiro.',
          },
          {
            kind: 'p',
            text: 'A substituição do modelo atual pelo IBS e pela CBS altera a lógica tributária não apenas da operação da escola, mas da forma como o mantenedor extrai renda do negócio e organiza seu patrimônio pessoal. Estruturas societárias que hoje são eficientes podem deixar de ser. Estratégias de distribuição de lucros precisarão ser revistas. E decisões tomadas agora — sobre holdings, imóveis, participações societárias — terão consequências que se estenderão por décadas.',
          },
          {
            kind: 'p',
            text: 'A Educa Legal, em parceria com a LG Contábil, trabalha exatamente nessa interseção: o planejamento tributário da escola e o planejamento patrimonial do mantenedor não são tratados como dois temas separados, mas como duas faces da mesma decisão estratégica. Porque proteger a escola sem proteger o patrimônio de quem a construiu é uma visão incompleta — e cara no longo prazo.',
          },
        ],
      },
      {
        heading: 'Visão integrada: escola, negócio e patrimônio pessoal na mesma mesa',
        blocks: [
          {
            kind: 'p',
            text: 'A Educa Legal atua em parceria com a LG Contábil, especializada em gestão patrimonial para o setor educacional. Essa combinação permite que o mantenedor tenha, em um único ponto de contato, uma leitura simultânea de três dimensões que a Reforma Tributária afeta ao mesmo tempo:',
          },
          {
            kind: 'list',
            items: [
              {
                term: 'a operação da escola',
                text: 'como a mudança impacta custos, contratos e precificação de mensalidades;',
              },
              {
                term: 'a estrutura societária do negócio',
                text: 'se a organização jurídica atual ainda é a mais eficiente sob o novo regime;',
              },
              {
                term: 'o patrimônio pessoal e familiar',
                text: 'como proteger, organizar e fazer crescer o que foi construído ao longo dos anos, sem que a transição tributária corroa o que já foi conquistado.',
              },
            ],
          },
          {
            kind: 'p',
            text: 'Essa visão de 360° é o que diferencia a abordagem da Educa Legal de uma consultoria tributária tradicional — que olha para o fisco — ou de um escritório jurídico convencional — que olha para o contrato. Aqui, olhamos para o todo.',
          },
        ],
      },
    ],
    cta: 'Quero entender o impacto da Reforma Tributária na minha escola e no meu patrimônio',
  },
  {
    slug: 'juridico-interno-escolas',
    title: 'Jurídico Interno para Escolas',
    seoTitle: 'Jurídico Interno para Escolas | Educa Legal',
    tagline:
      'Seu departamento jurídico dedicado — sem precisar contratar um advogado CLT. Suporte contínuo para todas as demandas legais do dia a dia da sua instituição.',
    tone: 'orange',
    h1: 'Jurídico Interno para Escolas — Uma Consultoria que Conhece a Operação por Dentro',
    metaDescription:
      'Departamento jurídico dedicado à sua escola, sem custo de CLT. Suporte contínuo, acesso direto e resposta ágil para gestores e mantenedores.',
    intro: [
      'A maioria dos advogados que atende escolas conhece o direito. Poucos conhecem a escola. Sabem redigir um contrato de trabalho — mas não sabem o que é uma CCT de professores com 80 cláusulas específicas para o setor. Sabem defender uma ação judicial — mas chegam ao problema depois que ele já custou dinheiro, energia e reputação para a instituição.',
      'O modelo de Jurídico Interno da Educa Legal parte de uma premissa diferente: um advogado só consegue resolver bem os problemas de uma escola quando entende como ela funciona por dentro — a dinâmica com as famílias, a pressão sobre o mantenedor, a complexidade de gerir pessoas em um ambiente pedagógico, as exigências dos órgãos reguladores do setor. Esse entendimento não se constrói estudando processos. Ele vem de anos vivendo e assessorando o setor educacional de dentro.',
    ],
    offers: [
      'Advogado responsável fixo, com conhecimento aprofundado do setor educacional e acesso direto por WhatsApp, e-mail ou videochamada',
      'Suporte contínuo para todas as demandas jurídicas do dia a dia — contratos com famílias, relações trabalhistas, dúvidas operacionais, revisão de documentos',
      'Atuação preventiva e proativa — alertas sobre mudanças legislativas antes que impactem sua operação, não depois',
      'Consultoria estratégica integrada ao ritmo da escola — disponível para a consulta rápida antes de uma decisão, não apenas para o incêndio depois que ele começa',
      'Respostas ágeis — porque gestores escolares não operam apenas em horário comercial e não podem aguardar dias por uma orientação',
      'Relatórios periódicos com status das demandas e alertas do setor — para que o mantenedor tenha visibilidade jurídica da sua operação sem precisar ser especialista',
    ],
    sections: [
      {
        heading: 'A diferença entre um advogado que atende escolas e um especialista que conhece escolas',
        blocks: [
          {
            kind: 'p',
            text: 'Um advogado generalista atende sua escola da mesma forma que atende um escritório de contabilidade, uma construtora ou um restaurante. Ele aplica o direito ao problema que você leva até ele — mas não sabe o que perguntar antes, não conhece os riscos específicos do seu setor e não consegue antecipar o que ainda não virou problema.',
          },
          {
            kind: 'p',
            text: 'O especialista da Educa Legal chega com um repertório construído dentro do setor educacional: conhece as particularidades da CCT regional dos professores, sabe como funcionam os órgãos reguladores estaduais e municipais, entende a dinâmica de uma reclamação de família antes que ela vire uma notificação, e conhece os padrões de risco que se repetem em escolas de diferentes portes e regiões do Brasil.',
          },
          {
            kind: 'p',
            text: 'Essa diferença se manifesta no dia a dia de formas concretas. Quando o mantenedor diz "preciso demitir um professor por justa causa", o especialista já sabe quais são os pontos críticos, quais documentos precisam estar em ordem e qual o risco real da decisão. Quando chega uma notificação de uma família sobre inclusão, o especialista já conhece a legislação aplicável, a jurisprudência do setor e o que costuma resolver antes de chegar ao judicial.',
          },
          {
            kind: 'p',
            text: 'Não é só conhecimento técnico. É familiaridade com a realidade de quem você atende.',
          },
        ],
      },
      {
        heading: 'Para quem é o modelo de Jurídico Interno da Educa Legal',
        body: 'Este modelo foi desenhado para escolas que tomam decisões jurídicas relevantes todos os dias — sobre contratos com famílias, admissões, demissões, políticas internas, relacionamento com fornecedores e reguladores — e que precisam de um parceiro que entenda essas decisões no contexto de quem gerencia uma instituição de ensino, não no contexto genérico do direito.',
      },
      {
        heading: 'Por que a Educa Legal',
        blocks: [
          {
            kind: 'p',
            text: 'A Educa Legal foi fundada por Pedro Guimarães Neto, advogado com mais de 11 anos dedicados ao setor educacional e com trajetória executiva dentro de empresas como Somos Educação, Isaac e Arco Educação. Essa experiência de dentro — na operação, na gestão de pessoas, nas relações com reguladores e famílias — é o que estrutura a forma como o escritório assessora cada cliente.',
          },
          {
            kind: 'p',
            text: 'Os advogados da Educa Legal possuem repertório construído a partir de anos de imersão no mercado educacional brasileiro, o que se reflete em uma bagagem acumulada e uma familiaridade com a realidade dos clientes.',
          },
          {
            kind: 'p',
            text: 'Quando a demanda exige uma especialidade que vai além do escopo do jurídico interno, a Educa Legal aciona sua rede de parceiros especialistas — mas mantém a coordenação, o acompanhamento e a comunicação centralizada. O mantenedor nunca fica sem referência.',
          },
        ],
      },
    ],
    cta: 'Quero um jurídico que conhece a minha escola por dentro',
  },
  {
    slug: 'direito-trabalhista-educacional',
    title: 'Direito Trabalhista Educacional',
    seoTitle: 'Direito Trabalhista Educacional | Educa Legal',
    tagline:
      'Gestão jurídica das relações com professores, colaboradores e sindicatos — com domínio da CCT regional e da legislação específica do setor educacional.',
    tone: 'sky',
    h1: 'Direito Trabalhista Educacional — Assessoria Jurídica para Escolas em Todo o Brasil',
    metaDescription:
      'Assessoria trabalhista para escolas: CCT, demissões, sindicatos docentes e contratos de professores. Especialistas em legislação educacional.',
    intro: [
      'A relação trabalhista em escolas privadas é uma das mais complexas do mercado brasileiro — e uma das mais negligenciadas. Professores têm regime jurídico próprio, jornadas calculadas em hora-aula, adicionais por titulação e regras de aviso prévio que diferem da CLT geral. Os sindicatos docentes acompanham de perto o cumprimento de cada cláusula. E a Convenção Coletiva de Trabalho dos professores varia por estado, por categoria e por ano — o que significa que a escola que não tem assessoria especializada está constantemente exposta a passivos que não consegue enxergar.',
      'A Educa Legal atua com assessoria trabalhista especializada para instituições de ensino em todo o território brasileiro — com domínio das convenções coletivas de professores nos principais estados do país e conhecimento profundo das especificidades que tornam o direito trabalhista educacional uma área à parte dentro do direito do trabalho.',
    ],
    offers: [
      'Assessoria jurídica em admissões, demissões e rescisões de professores e colaboradores — com ou sem justa causa, respeitando as especificidades da CCT aplicável',
      'Leitura, interpretação e aplicação da Convenção Coletiva de Trabalho regional — garantindo que a escola esteja em conformidade com as cláusulas vigentes em cada estado',
      'Suporte em negociações com sindicatos docentes e de funcionários administrativos',
      'Revisão de contratos de trabalho, termos aditivos, acordos individuais e banco de horas pedagógicas',
      'Orientação em casos de afastamento, licença médica, licença maternidade e gestão de benefícios específicos do setor',
      'Defesa em reclamações trabalhistas e acompanhamento estratégico de processos',
      'Estruturação de processos internos de gestão de pessoas para redução do risco trabalhista desde a origem',
    ],
    sections: [
      {
        heading: 'Atuação em todo o Brasil — com domínio das CCTs regionais',
        blocks: [
          {
            kind: 'p',
            text: 'Cada estado brasileiro tem sua própria dinâmica de negociação coletiva no setor educacional — e ignorar as especificidades locais é uma das principais fontes de passivo trabalhista para escolas privadas.',
          },
          {
            kind: 'p',
            text: 'A Educa Legal atua com conhecimento das convenções coletivas de professores em todo o território nacional, com atenção especial aos estados de maior complexidade e volume de demandas:',
          },
          {
            kind: 'list',
            items: [
              {
                term: 'São Paulo',
                text: 'a CCT dos professores de escolas privadas em São Paulo é uma das mais detalhadas do país, com cláusulas específicas sobre jornada, hora-atividade, adicional por titulação e reajustes anuais que exigem acompanhamento constante. Erros na aplicação da CCT paulista estão entre as principais origens de reclamações trabalhistas no setor educacional.',
              },
              {
                term: 'Minas Gerais',
                text: 'o mercado educacional mineiro tem particularidades nas negociações sindicais que afetam diretamente escolas de Belo Horizonte e do interior. A Educa Legal tem histórico de atuação nesse estado e domínio da convenção coletiva aplicável às instituições de ensino privado.',
              },
              {
                term: 'Rio de Janeiro',
                text: 'a dinâmica sindical docente no Rio de Janeiro tem características próprias, com negociações que frequentemente incluem cláusulas sobre condições de trabalho que vão além da remuneração. Escolas que não acompanham essas negociações de perto tendem a acumular passivos silenciosos.',
              },
              {
                term: 'Paraná, Rio Grande do Sul e Santa Catarina',
                text: 'a região Sul concentra um número expressivo de instituições de ensino privado de médio e grande porte, com convenções coletivas que variam por estado e por nível de ensino. A Educa Legal assessora escolas nos três estados com conhecimento das CCTs regionais aplicáveis.',
              },
              {
                term: 'Goiás e Distrito Federal',
                text: 'o Centro-Oeste tem um mercado educacional em expansão acelerada, com crescimento de redes privadas que frequentemente subestimam os riscos trabalhistas da expansão rápida. Atuamos com assessoria preventiva e contenciosa para instituições dessas regiões.',
              },
            ],
          },
          {
            kind: 'p',
            text: 'Para demais estados e regiões do Brasil, a Educa Legal atua com o mesmo padrão de assessoria — adaptando a análise à CCT regional vigente e às particularidades do mercado educacional local.',
          },
        ],
      },
      {
        heading: 'Por que o direito trabalhista educacional exige especialização própria',
        blocks: [
          {
            kind: 'p',
            text: 'A legislação trabalhista geral já é complexa. Aplicada ao setor educacional, ela ganha camadas adicionais que um advogado trabalhista generalista frequentemente desconhece — e o custo de um erro aqui costuma ser alto.',
          },
          {
            kind: 'p',
            text: 'Professores horistas têm sua jornada calculada de forma diferente da CLT padrão. O regime de hora-aula determina como o aviso prévio é calculado. O banco de horas pedagógicas tem regras próprias que variam conforme a CCT. O adicional por titulação — especialização, mestrado, doutorado — precisa ser aplicado corretamente desde a admissão, sob risco de gerar passivo retroativo. A estabilidade gestante em período de contrato determinado tem entendimento jurisprudencial específico para professores.',
          },
          {
            kind: 'p',
            text: 'Cada um desses pontos é uma fonte potencial de reclamação trabalhista — e cada um deles exige que o advogado responsável conheça não apenas o direito do trabalho, mas a realidade de quem trabalha em uma escola.',
          },
        ],
      },
      {
        heading: 'O risco de não ter assessoria trabalhista especializada',
        blocks: [
          {
            kind: 'p',
            text: 'Escolas que operam sem suporte trabalhista especializado tendem a acumular passivos de três formas distintas: erros na aplicação da CCT que só aparecem em uma reclamação anos depois, demissões mal conduzidas que se tornam processos com alto risco de condenação, e ausência de documentação adequada que inverte o ônus da prova em favor do colaborador.',
          },
          {
            kind: 'p',
            text: 'O custo médio de uma reclamação trabalhista de professor, considerando honorários, condenação e tempo de gestão interna, frequentemente supera em muito o que seria investido em assessoria preventiva ao longo de anos.',
          },
        ],
      },
      {
        heading: 'Por que a Educa Legal',
        blocks: [
          {
            kind: 'p',
            text: 'A Educa Legal foi fundada por Pedro Guimarães Neto, advogado dedicado desde 2015 exclusivamente ao setor educacional e com trajetória executiva dentro de grandes empresas do setor. Esse conhecimento de dentro — da operação, da cultura organizacional de escolas e da dinâmica das relações com professores e sindicatos — é o que diferencia nossa assessoria trabalhista de um escritório que eventualmente atende escolas.',
          },
          {
            kind: 'p',
            text: 'Nosso objetivo não é apenas defender a escola quando o problema aparece. É estruturar os processos de gestão de pessoas para que o risco trabalhista seja identificado e minimizado antes de virar processo — porque escola bem assessorada não apaga incêndio, previne incêndio.',
          },
        ],
      },
    ],
    cta: 'Quero assessoria trabalhista especializada para minha escola',
    relatedPostSlugs: ['piso-salarial-magisterio-impacto'],
  },
  {
    slug: 'contratos-inadimplencia-escolar',
    title: 'Contratos e Inadimplência Escolar',
    seoTitle: 'Contratos de Matrícula e Inadimplência Escolar | Educa Legal',
    tagline:
      'Contratos de matrícula robustos e gestão de inadimplência de mensalidades — com foco em prevenção de conflitos e recuperação ágil de receita.',
    tone: 'grape',
    h1: 'Contratos, Inadimplência e Reajuste de Mensalidades Escolares | Educa Legal',
    metaDescription:
      'Contratos de matrícula, gestão de inadimplência e reajuste de mensalidades escolares. Assessoria jurídica especializada para proteger a receita da sua instituição.',
    intro:
      'O contrato de matrícula é o documento mais importante da relação entre a escola e as famílias — e também o mais negligenciado. Contratos mal redigidos, com cláusulas ambíguas ou em desacordo com a legislação do consumidor, são a principal origem de conflitos com responsáveis e de perdas na cobrança de mensalidades em atraso.',
    offers: [
      'Elaboração e revisão de contratos de matrícula em conformidade com o CDC e a legislação educacional',
      'Modelagem de política de inadimplência — régua de cobrança, notificações e critérios de negociação',
      'Suporte jurídico em conflitos com responsáveis sobre reajustes, mensalidades e cancelamentos',
      'Assessoria no processo anual de reajuste de mensalidades — definição do índice aplicável, elaboração do comunicado às famílias e estruturação do processo dentro dos limites legais do CDC',
      'Assessoria em ações de cobrança judicial e extrajudicial de mensalidades em atraso',
      'Revisão de contratos com fornecedores e prestadores de serviço da instituição',
    ],
    sections: [
      {
        heading: 'Reajuste de mensalidades — como fazer certo e evitar conflitos com as famílias',
        blocks: [
          {
            kind: 'p',
            text: 'O reajuste anual de mensalidades é um dos momentos de maior tensão na relação entre escola e família — e um dos que mais gera dúvidas jurídicas para os mantenedores. Quanto posso reajustar? Qual índice usar? Com quanto de antecedência devo comunicar? O que fazer quando a família se recusa a assinar o novo contrato?',
          },
          {
            kind: 'p',
            text: 'Não existe um teto legal fixo para o reajuste de mensalidades escolares no Brasil — mas o Código de Defesa do Consumidor impõe limites importantes sobre como esse reajuste pode ser aplicado e comunicado. Reajustes abusivos, comunicados intempestivos ou alterações contratuais sem anuência da família são as principais origens de ações judiciais e reclamações em órgãos de defesa do consumidor no setor educacional.',
          },
          {
            kind: 'p',
            text: 'A Educa Legal assessora escolas em todo o processo de reajuste anual: desde a escolha do índice mais adequado ao perfil da instituição — IPCA, INPC ou índice setorial — até a elaboração do comunicado às famílias em linguagem clara e juridicamente embasada, passando pela estruturação do aditivo contratual que formaliza o novo valor de forma válida e defensável.',
          },
          {
            kind: 'p',
            text: 'O objetivo não é apenas reajustar dentro da lei. É fazer o processo de forma que as famílias entendam, aceitem e continuem confiando na instituição — porque conflito no reajuste de mensalidade raramente fica só no financeiro. Ele afeta a reputação da escola, a relação com a família e, não raro, termina em cancelamento de matrícula.',
          },
        ],
      },
      {
        heading: 'O impacto da inadimplência sem estrutura jurídica',
        body: 'A inadimplência escolar no Brasil afeta diretamente o fluxo de caixa e a capacidade de investimento das instituições. Sem uma política clara e juridicamente embasada, a escola oscila entre dois extremos igualmente problemáticos: cobranças que expõem a instituição a processos por constrangimento ilegal, ou tolerância excessiva que estimula o não pagamento. A Educa Legal ajuda a estruturar um caminho do meio — eficiente, legal e sustentável.',
      },
      {
        heading: 'Por que a Educa Legal',
        body: 'Conhecemos as particularidades da relação contratual entre escolas e famílias — inclusive os limites impostos pelo Código de Defesa do Consumidor e pela jurisprudência sobre reajuste de mensalidades. Nossos contratos são desenhados para proteger a receita da escola sem criar atritos desnecessários com as famílias.',
      },
    ],
    cta: 'Quero revisar os contratos da minha escola',
  },
  {
    slug: 'lgpd-escolas',
    title: 'LGPD para Escolas',
    seoTitle: 'LGPD para Escolas | Adequação e Consultoria | Educa Legal',
    tagline:
      'Adequação completa à Lei Geral de Proteção de Dados — da coleta de dados de alunos e responsáveis até os processos internos dos colaboradores.',
    tone: 'olive',
    h1: 'LGPD para Escolas — Adequação Completa à Lei Geral de Proteção de Dados',
    metaDescription:
      'Adequação completa à LGPD para escolas: dados de alunos, responsáveis e colaboradores. Consultoria especializada em instituições de ensino.',
    intro:
      'Escolas coletam e tratam dados sensíveis de forma intensiva: informações de saúde de alunos, laudos médicos, dados de responsáveis, registros de comportamento, imagens de câmeras. Toda essa operação está sujeita à Lei Geral de Proteção de Dados — e a maioria das instituições de ensino ainda opera em desconformidade, exposta a multas e a ações judiciais de famílias.',
    offers: [
      'Diagnóstico de conformidade — mapeamento de todos os fluxos de dados da instituição',
      'Elaboração de política de privacidade e termos de consentimento adequados ao contexto escolar',
      'Revisão e adequação dos contratos com fornecedores que acessam dados da escola',
      'Treinamento da equipe pedagógica e administrativa sobre boas práticas de proteção de dados',
      'Suporte contínuo para dúvidas operacionais e atualização frente a novas regulamentações da ANPD',
      'Resposta a incidentes de segurança e notificações à autoridade reguladora',
    ],
    sections: [
      {
        heading: 'Por que a LGPD é especialmente crítica para escolas',
        body: 'Diferente de outros setores, escolas tratam dados de crianças e adolescentes — categoria que exige proteção reforçada pela LGPD. Além disso, laudos médicos, relatórios psicológicos e registros de comportamento são dados sensíveis cujo tratamento inadequado expõe a instituição a consequências graves, tanto na esfera administrativa quanto judicial.',
      },
      {
        heading: 'Por que a Educa Legal',
        body: 'Nossa abordagem em LGPD é prática e voltada à operação real da escola — não apenas à produção de documentos que ficam na gaveta. Trabalhamos para que a conformidade seja incorporada aos processos do dia a dia da instituição, de forma simples e sustentável.',
      },
    ],
    cta: 'Quero adequar minha escola à LGPD',
  },
  {
    slug: 'inclusao-escolar-legislacao',
    title: 'Inclusão Escolar e Legislação',
    seoTitle: 'Inclusão Escolar — Consultoria Jurídica e PEI | Educa Legal',
    tagline:
      'Consultoria jurídica e treinamento para construir políticas de inclusão e acolhimento para alunos neuroatípicos — em conformidade com a legislação educacional vigente.',
    tone: 'mustard',
    h1: 'Inclusão Escolar — TEA, TDAH, Dislexia e Lei nº 13.146/2015 | Educa Legal',
    metaDescription:
      'Consultoria jurídica para inclusão escolar: TEA, TDAH, dislexia e PEI. Conheça as obrigações e limites da Lei Brasileira de Inclusão e proteja sua escola.',
    intro:
      'A legislação de inclusão escolar no Brasil avançou significativamente nos últimos anos — e as obrigações das escolas privadas aumentaram na mesma proporção. Recusar matrícula, oferecer suporte inadequado ou não elaborar corretamente o Plano de Ensino Individualizado (PEI) são situações que expõem a instituição a processos judiciais, multas e danos reputacionais — e estão em desacordo com a Lei Brasileira de Inclusão, Lei nº 13.146/2015, que estabelece as obrigações das escolas privadas em relação a alunos com deficiência e neuroatipicidades de forma clara e exigível.',
    offers: [
      'Consultoria jurídica sobre as obrigações legais da escola em relação a alunos com deficiência e neuroatipicidades',
      'Suporte na elaboração e revisão de Planos de Ensino Individualizados (PEI) sob perspectiva legal',
      'Treinamento de equipes pedagógicas e administrativas sobre direitos dos alunos com deficiência',
      'Orientação em conflitos com famílias sobre adaptações, suporte especializado e matrícula',
      'Atualização contínua sobre mudanças na legislação de inclusão educacional',
    ],
    sections: [
      {
        heading: 'O cenário atual',
        body: 'O número de diagnósticos de Transtorno do Espectro Autista (TEA), Transtorno do Déficit de Atenção com Hiperatividade (TDAH), dislexia, Transtorno Opositivo Desafiador (TOD) e outras neuroatipicidades cresceu expressivamente nos últimos anos — e com ele, o volume de demandas jurídicas relacionadas à inclusão. Famílias de alunos com TEA, TDAH e dislexia estão cada vez mais informadas sobre os direitos garantidos pela Lei nº 13.146/2015 e pela jurisprudência consolidada dos tribunais brasileiros. Escolas que não têm políticas claras e juridicamente embasadas para cada uma dessas condições estão progressivamente mais expostas.',
      },
      {
        heading: 'Quais condições a legislação de inclusão escolar abrange — e o que sua escola precisa saber',
        blocks: [
          {
            kind: 'p',
            text: 'A Lei Brasileira de Inclusão, combinada com a jurisprudência dos tribunais brasileiros, cobre um espectro amplo de condições que as escolas privadas são obrigadas a acolher e adaptar. As demandas jurídicas mais frequentes que a Educa Legal acompanha envolvem:',
          },
          {
            kind: 'list',
            items: [
              {
                term: 'Transtorno do Espectro Autista (TEA)',
                text: 'escolas não podem recusar matrícula a alunos com autismo, independentemente do grau. A obrigação de oferecer acompanhante terapêutico, adaptações pedagógicas e suporte adequado está consolidada na jurisprudência. Escolas que não estruturam essa política correm risco real de ações judiciais e condenações.',
              },
              {
                term: 'TDAH',
                text: 'o Transtorno do Déficit de Atenção com Hiperatividade não é motivo legal para recusa de matrícula, transferência compulsória ou qualquer forma de discriminação. A escola que não adapta seu processo de avaliação e seu ambiente para alunos com TDAH está exposta a processos tanto na esfera cível quanto perante o Ministério Público.',
              },
              {
                term: 'Dislexia e outros transtornos de aprendizagem',
                text: 'alunos com dislexia têm direito a adaptações nas formas de avaliação e nos materiais pedagógicos. A ausência de laudo não elimina a obrigação da escola de acolher e adaptar quando há indicação pedagógica clara.',
              },
              {
                term: 'Transtorno Opositivo Desafiador (TOD)',
                text: 'uma das condições que mais gera conflito entre escola e família, o TOD exige políticas internas claras sobre manejo comportamental que estejam alinhadas com a legislação e com os direitos do aluno.',
              },
            ],
          },
          {
            kind: 'p',
            text: 'Para todas essas condições, o Plano de Ensino Individualizado (PEI) bem elaborado é o principal instrumento de proteção jurídica da escola — e também o principal argumento de defesa em caso de questionamento judicial.',
          },
        ],
      },
      {
        heading: 'Por que a Educa Legal',
        body: 'Combinamos conhecimento jurídico com entendimento da realidade pedagógica das escolas. Nosso objetivo não é apenas proteger a instituição juridicamente — é ajudá-la a construir uma política de inclusão genuína, que atenda às famílias, proteja a escola e esteja em conformidade com a legislação.',
      },
      {
        heading: 'Inclusão não significa atender toda e qualquer exigência — e saber essa diferença protege sua escola',
        blocks: [
          {
            kind: 'p',
            text: 'A obrigação legal de matricular e incluir alunos com deficiência e neuroatipicidades é clara e inegociável. Mas a legislação brasileira de inclusão não determina que todas as demandas apresentadas pelas famílias sejam automaticamente atendidas — e confundir esses dois planos é uma das principais fontes de conflito desnecessário entre escolas e responsáveis.',
          },
          {
            kind: 'p',
            text: 'A Lei Brasileira de Inclusão e a jurisprudência dos tribunais reconhecem que adaptações razoáveis são exigíveis — mas que exigências que inviabilizem a operação escolar, que exijam recursos humanos ou materiais desproporcionais à realidade da instituição, ou que restrinjam a convivência do aluno com seus pares vão na contramão do próprio espírito da inclusão. A inclusão escolar genuína é aquela que integra — não a que isola o aluno em um atendimento individualizado que o separa do ambiente coletivo que a escola proporciona.',
          },
          {
            kind: 'p',
            text: 'Adaptações são necessárias dos dois lados. A escola tem obrigação de estruturar seu ambiente, seus processos pedagógicos e sua equipe para acolher alunos com diferentes perfis de desenvolvimento. As famílias, por sua vez, têm o papel de colaborar com esse processo dentro dos recursos e da capacidade real da instituição — e não toda exigência apresentada como direito do aluno encontra respaldo legal quando analisada com rigor jurídico.',
          },
          {
            kind: 'p',
            text: 'Esse equilíbrio é onde reside a maior complexidade prática da inclusão escolar — e é exatamente onde a Educa Legal atua com seus clientes. Cada caso é analisado individualmente: o diagnóstico do aluno, os recursos disponíveis na instituição, o que a legislação efetivamente exige naquela situação específica e o que pode ser negociado com a família de forma que seja viável para a escola e genuinamente benéfico para o desenvolvimento do jovem. O objetivo não é encontrar o mínimo que a escola pode fazer — é encontrar o que é juridicamente sustentável, operacionalmente viável e pedagogicamente adequado para aquela criança naquele contexto.',
          },
          {
            kind: 'p',
            text: 'Gestores que contam com esse tipo de orientação caso a caso tomam decisões mais seguras, constroem relações mais honestas com as famílias e evitam que conflitos evitáveis se transformem em processos judiciais desnecessários — para ambos os lados.',
          },
        ],
      },
    ],
    cta: 'Quero estruturar a política de inclusão da minha escola',
  },
  {
    slug: 'compliance-escolar',
    title: 'Compliance para Instituições de Ensino',
    seoTitle: 'Compliance Escolar — Governança e Canal de Denúncias | Educa Legal',
    tagline:
      'Criação de políticas internas, canal de denúncias e estruturas de governança que protegem a instituição, reduzem riscos e refletem seus valores.',
    tone: 'orange',
    h1: 'Compliance Escolar — Governança, Políticas Internas e Canal de Denúncias para Instituições de Ensino',
    metaDescription:
      'Compliance para escolas e instituições de ensino: código de conduta, canal de denúncias e governança. Proteja sua instituição com estrutura jurídica.',
    intro:
      'Compliance não é tema exclusivo de grandes corporações. Escolas privadas — especialmente as de médio e grande porte — precisam de estruturas de governança que protejam a instituição de riscos internos, garantam o cumprimento de obrigações legais e transmitam confiança para famílias, colaboradores e parceiros.',
    offers: [
      'Elaboração de código de conduta e políticas internas alinhadas à cultura e aos valores da instituição',
      'Implementação de canal de denúncias — estrutura, comunicação e gestão de casos',
      'Mapeamento de riscos jurídicos e operacionais da instituição',
      'Estruturação de processos de due diligence para contratação de fornecedores e parceiros',
      'Treinamento de lideranças sobre ética, compliance e gestão de riscos',
      'Suporte em investigações internas e gestão de crises reputacionais',
    ],
    sections: [
      {
        heading: 'Por que compliance é urgente para escolas',
        body: 'Casos de assédio, desvio de conduta, conflitos de interesse e irregularidades financeiras em escolas têm consequências que vão muito além do jurídico — afetam a reputação da instituição com famílias, comprometem a retenção de talentos e podem inviabilizar processos de captação ou venda. Ter estruturas de compliance não é custo: é proteção de valor.',
      },
      {
        heading: 'Por que a Educa Legal',
        body: 'Desenhamos programas de compliance que consideram a realidade de cada instituição — seu porte, sua cultura, suas vulnerabilidades específicas. Não entregamos documentos genéricos: construímos estruturas que funcionam na prática e que a equipe consegue operar sem depender de suporte jurídico constante.',
      },
    ],
    cta: 'Quero estruturar o compliance da minha instituição',
    relatedPostSlugs: ['credenciamento-mec-guia-2024'],
  },
  {
    slug: 'direito-imobiliario-escolas',
    title: 'Direito Imobiliário para Escolas',
    seoTitle: 'Direito Imobiliário para Escolas | Regularização e Locação | Educa Legal',
    tagline:
      'Regularização de imóveis, análise de contratos de locação e suporte jurídico para todas as questões patrimoniais ligadas à operação escolar.',
    tone: 'sky',
    h1: 'Direito Imobiliário para Escolas — Alvará, Regularização e Locação | Educa Legal',
    metaDescription:
      'Alvará de funcionamento, regularização de imóveis e contratos de locação para escolas. Assessoria jurídica imobiliária especializada em instituições de ensino.',
    intro:
      'O patrimônio imobiliário é um dos maiores ativos — e um dos maiores riscos — de uma escola privada. Imóveis irregulares, contratos de locação com cláusulas desfavoráveis ou mal negociados, e operações de compra sem due diligence adequada são fontes frequentes de passivo e de instabilidade operacional para as instituições.',
    offers: [
      'Regularização de imóveis próprios — análise de matrícula, averbações e pendências junto aos cartórios',
      'Revisão e negociação de contratos de locação comercial para uso escolar',
      'Suporte jurídico em renovações, rescisões e disputas com locadores',
      'Assessoria em aquisição de imóveis para expansão ou instalação de novas unidades',
      'Análise de contratos de construção, reforma e prestação de serviços de engenharia',
      'Suporte em regularização junto a prefeituras e órgãos de vigilância sanitária',
      'Assessoria na obtenção e renovação de alvará de funcionamento escolar — análise dos requisitos municipais, interface com órgãos públicos e suporte documental em todo o processo',
      'Orientação sobre habite-se e regularização de edificações — identificação de pendências que podem impedir a renovação do alvará ou inviabilizar uma futura venda ou captação de investimento',
    ],
    sections: [
      {
        heading: 'O risco imobiliário subestimado',
        body: 'Muitas escolas operam em imóveis com pendências de regularização há anos — sem perceber que isso representa um risco real de interdição, de dificuldade em renovar alvarás e de impedimento em processos de venda ou captação de investimento. Da mesma forma, contratos de locação mal negociados frequentemente prendem a instituição a condições desfavoráveis por anos.',
      },
      {
        heading: 'Alvará de funcionamento e habite-se — as questões imobiliárias mais urgentes para escolas',
        blocks: [
          {
            kind: 'p',
            text: 'Para a maioria dos gestores escolares, a questão imobiliária mais concreta e imediata não é a regularização da matrícula do imóvel no cartório — é o alvará de funcionamento e a regularidade das edificações onde a escola opera.',
          },
          {
            kind: 'list',
            items: [
              {
                term: 'Alvará de funcionamento escolar',
                text: 'cada município tem requisitos próprios para a concessão e renovação do alvará de escolas privadas. Metragem mínima por aluno, número de banheiros, acessibilidade, saídas de emergência, laudos de vistoria do corpo de bombeiros — são exigências que variam por cidade e que, quando não atendidas, resultam em notificações, multas e, em casos extremos, interdição. A Educa Legal assessora escolas no mapeamento dessas exigências, na regularização de pendências e no processo de renovação junto às prefeituras, minimizando o risco de surpresas.',
              },
              {
                term: 'Habite-se e regularização de edificações',
                text: 'muitas escolas operam em imóveis com ampliações, reformas ou construções realizadas sem a devida aprovação na prefeitura. Essa irregularidade raramente aparece no dia a dia operacional — mas se manifesta com força em dois momentos: quando a escola tenta vender o imóvel ou quando precisa renovar o alvará de funcionamento. A regularização dessas edificações, embora burocrática, é um processo que precisa ser conduzido com orientação jurídica para evitar autuações e garantir que a documentação produzida seja válida para todos os fins legais.',
              },
            ],
          },
          {
            kind: 'p',
            text: 'A combinação de alvará irregular e edificações sem habite-se é uma das situações de risco imobiliário mais comuns em escolas privadas brasileiras — e uma das mais silenciosas, porque não gera impacto visível até que alguém pergunte pela documentação.',
          },
        ],
      },
      {
        heading: 'Por que a Educa Legal',
        body: 'A combinação de conhecimento jurídico com experiência no setor educacional nos permite analisar questões imobiliárias sob a perspectiva de quem entende a operação de uma escola — não apenas sob a perspectiva do direito imobiliário tradicional.',
      },
    ],
    cta: 'Quero uma análise da situação imobiliária da minha escola',
  },
  {
    slug: 'gestao-contencioso-escolar',
    title: 'Gestão Estratégica de Contencioso',
    seoTitle: 'Gestão de Contencioso Escolar | Redução de Passivo Jurídico | Educa Legal',
    tagline:
      'Coordenação estratégica das ações judiciais da sua instituição — selecionando os melhores especialistas para cada tipo de demanda e reduzindo o passivo jurídico da escola.',
    tone: 'grape',
    h1: 'Gestão Estratégica de Contencioso Escolar — Redução de Passivo Jurídico | Educa Legal',
    metaDescription:
      'Gestão do contencioso escolar: coordenação estratégica de ações trabalhistas, cíveis e administrativas. Reduza passivos e custos com especialistas em direito educacional.',
    intro:
      'Toda escola de médio e grande porte acumula, ao longo do tempo, um volume de ações judiciais que precisa ser gerenciado de forma estratégica — não apenas respondido caso a caso. Sem coordenação, o contencioso vira um custo crescente e imprevisível, com processos conduzidos por advogados diferentes, sem visão consolidada do passivo e sem estratégia de redução. O resultado é previsível: gastos com honorários que se acumulam sem critério, acordos firmados sem análise de precedente e passivos que crescem silenciosamente até aparecerem no balanço como um problema sem solução simples.',
    offers: [
      'Mapeamento e diagnóstico do contencioso atual da instituição — trabalhista, cível e administrativo',
      'Seleção e coordenação de advogados especialistas para cada tipo de demanda',
      'Acompanhamento estratégico dos processos em andamento, com relatórios periódicos',
      'Definição de estratégia de defesa e critérios de acordo para cada tipo de ação',
      'Análise de provisões e impacto financeiro do contencioso',
      'Trabalho preventivo para redução do volume de novas ações',
    ],
    sections: [
      {
        heading: 'O custo real de um contencioso não gerenciado',
        blocks: [
          {
            kind: 'p',
            text: 'Escolas que não gerenciam seu contencioso de forma estratégica tendem a subestimar o custo real das ações judiciais em dois aspectos que raramente aparecem juntos na mesma análise.',
          },
          {
            kind: 'p',
            text: 'O primeiro é o custo direto e visível: honorários advocatícios, condenações, custas processuais e provisões contábeis. Esse custo é mensurável — e frequentemente surpreende quando somado ao final do ano.',
          },
          {
            kind: 'p',
            text: 'O segundo é o custo indireto e invisível: o tempo do mantenedor e da equipe administrativa dedicado a responder demandas, reunir documentos, participar de audiências e coordenar advogados diferentes que não se comunicam entre si. Esse custo nunca aparece no balanço — mas está lá, drenando energia e atenção que deveriam estar no crescimento da instituição.',
          },
          {
            kind: 'p',
            text: 'A gestão estratégica do contencioso atua nos dois fronts simultaneamente. No direto, pela definição de estratégias de defesa mais eficientes e critérios claros de acordo que evitam condenações desnecessárias. No indireto, pela centralização da coordenação em um único ponto de contato — eliminando a fragmentação que multiplica o custo de gestão.',
          },
        ],
      },
      {
        heading: 'A lógica da gestão estratégica',
        body: 'Ter advogados diferentes para cada processo, sem coordenação central, significa perder inteligência. Um padrão de ações trabalhistas recorrentes, por exemplo, pode indicar um problema estrutural na gestão de pessoas — que, se corrigido, reduz o passivo futuro. A Educa Legal atua como o gestor jurídico do contencioso, com visão consolidada e foco em redução de custo e de risco.',
      },
      {
        heading: 'O que uma visão consolidada do contencioso revela que processos isolados escondem',
        blocks: [
          {
            kind: 'p',
            text: 'Quando cada processo é gerenciado por um advogado diferente, sem comunicação entre eles, a escola perde a inteligência que o conjunto dos casos poderia gerar.',
          },
          {
            kind: 'p',
            text: 'Um padrão de reclamações trabalhistas recorrentes sobre o mesmo tema — banco de horas, adicional por titulação, aviso prévio de professores — indica um problema estrutural na gestão de pessoas que, se corrigido, reduz o volume de novas ações. Mas esse padrão só fica visível quando alguém está olhando para o todo, não para cada caso isoladamente.',
          },
          {
            kind: 'p',
            text: 'Da mesma forma, um padrão de reclamações de famílias sobre o mesmo tipo de cláusula contratual indica uma vulnerabilidade no contrato de matrícula que pode ser corrigida na próxima renovação — evitando que o mesmo problema se repita nos próximos ciclos.',
          },
          {
            kind: 'p',
            text: 'A Educa Legal, ao assumir a coordenação estratégica do contencioso, produz relatórios periódicos que identificam esses padrões e traduzem o que os processos estão dizendo sobre a operação da escola. Porque contencioso bem gerenciado não é só sobre ganhar ou perder — é sobre aprender o que os processos revelam e usar essa inteligência para que os próximos não existam.',
          },
        ],
      },
      {
        heading: 'Por que a Educa Legal',
        body: 'Nossa rede de parceiros especialistas cobre todas as áreas do direito relevantes para o setor educacional. Quando acionamos um especialista externo, mantemos a coordenação do caso e garantimos que a estratégia adotada está alinhada com os interesses da instituição.',
      },
    ],
    cta: 'Quero uma análise do contencioso da minha escola',
  },
  {
    slug: 'ma-educacional',
    title: 'Compra e Venda de Escolas — M&A Educacional',
    seoTitle: 'M&A Educacional — Compra e Venda de Escolas | Educa Legal',
    tagline:
      'Assessoria jurídica completa para aquisição, venda e fusão de escolas e redes de ensino — due diligence, estruturação da operação e segurança em cada etapa da transação.',
    tone: 'mustard',
    h1: 'M&A Educacional — Assessoria Jurídica para Compra, Venda e Fusão de Escolas e Redes de Ensino',
    metaDescription:
      'Assessoria jurídica completa para compra, venda e fusão de escolas. Due diligence, estruturação e segurança em transações educacionais no Brasil.',
    intro:
      'O mercado educacional brasileiro vive um intenso movimento de consolidação. Redes nacionais expandem por aquisições, mantenedores independentes recebem propostas de grupos maiores, e investidores olham para o setor com interesse crescente. Nesse contexto, ter assessoria jurídica especializada em M&A educacional não é diferencial — é condição para que a transação seja segura e bem-sucedida.',
    offers: [
      'Due diligence jurídica completa — trabalhista, fiscal, contratual, regulatória e imobiliária',
      'Estruturação jurídica da operação — definição do modelo mais adequado para a transação',
      'Revisão e negociação de contratos de compra e venda, cartas de intenção e acordos de acionistas',
      'Suporte no relacionamento com reguladores — MEC, conselhos estaduais e municipais',
      'Assessoria em processos de captação de investimento e entrada de sócios',
      'Acompanhamento do pós-closing — integração jurídica e gestão de contingências',
    ],
    sections: [
      {
        heading: 'O que está em jogo numa transação educacional',
        body: 'M&A no setor educacional tem especificidades que tornam a due diligence mais complexa do que em outros segmentos: passivos trabalhistas de professores, contratos de matrícula vigentes, autorizações regulatórias, imóveis com pendências, e a reputação da instituição com as famílias. Uma transação mal assessorada pode transformar uma aquisição promissora em um passivo de anos.',
      },
      {
        heading: 'Por que a Educa Legal',
        body: 'Pedro Guimarães Neto construiu parte de sua trajetória executiva dentro de empresas do setor educacional que passaram por processos de M&A. Esse conhecimento de dentro — da operação, da cultura e dos riscos específicos do setor — é o que diferencia nossa assessoria de um escritório jurídico generalista que eventualmente atende transações educacionais.',
      },
    ],
    cta: 'Estou avaliando uma transação e quero conversar',
    relatedPostSlugs: ['ma-educacional-due-diligence'],
  },
];

export const servicePath = (slug: string) => `/servicos/${slug}`;

export function getService(slug: string): Service | undefined {
  return services.find((s) => s.slug === slug);
}
